import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ApiError, fetchSession, login as apiLogin, logout as apiLogout, onUnauthorized } from './api';
import type { SessionInfo } from '../types';

/**
 * Sessao do painel (RNF12).
 *
 * Um provedor no topo da arvore descobre quem esta logado (`GET /api/auth/me`)
 * antes de qualquer tela renderizar, e `RequireAuth` decide entre a pagina
 * pedida e o login. Tres estados, e o terceiro e o que evita o pisca-pisca:
 * enquanto `checking`, nada e redirecionado -- mandar para o login antes de
 * saber se ha sessao faria todo F5 passar pela tela de entrada.
 *
 * O 401 global do cliente HTTP desagua aqui: uma sessao que vence no meio do
 * uso vira `anonymous` e o `RequireAuth` da rota atual leva ao login, guardando
 * de onde a pessoa veio para voltar depois.
 */

export type AuthState =
  | { readonly status: 'checking' }
  | { readonly status: 'anonymous' }
  | { readonly status: 'authenticated'; readonly session: SessionInfo };

interface AuthContextValue {
  readonly state: AuthState;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { readonly children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>({ status: 'checking' });

  useEffect(() => {
    const controller = new AbortController();
    fetchSession(controller.signal)
      .then((session) => {
        if (controller.signal.aborted) {
          return;
        }
        setState(session === null ? { status: 'anonymous' } : { status: 'authenticated', session });
      })
      .catch(() => {
        // Servidor fora: sem sessao confirmada, o login e o unico lugar seguro
        // para estar -- e ele mostra o erro de rede ao tentar entrar.
        if (!controller.signal.aborted) {
          setState({ status: 'anonymous' });
        }
      });
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => onUnauthorized(() => {
    setState({ status: 'anonymous' });
  }), []);

  const login = useCallback(async (email: string, password: string): Promise<void> => {
    const session = await apiLogin(email, password);
    setState({ status: 'authenticated', session });
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await apiLogout();
    } catch (error) {
      // Rede fora no logout: o cookie e HttpOnly e nao da para apaga-lo daqui,
      // mas a interface sai mesmo assim; o servidor expira a sessao sozinho.
      if (!(error instanceof ApiError)) {
        throw error;
      }
    } finally {
      setState({ status: 'anonymous' });
    }
  }, []);

  const value = useMemo<AuthContextValue>(() => ({ state, login, logout }), [state, login, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (value === null) {
    throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  }
  return value;
}

/** Sessao garantida. So chame dentro de uma arvore protegida por RequireAuth. */
export function useSession(): SessionInfo {
  const { state } = useAuth();
  if (state.status !== 'authenticated') {
    throw new Error('useSession fora de area autenticada');
  }
  return state.session;
}

/**
 * Porteiro de rota. `checking` mostra uma tela neutra (nao o esqueleto da
 * pagina, que sugeriria que o dado esta vindo), `anonymous` vai para o login
 * lembrando o destino.
 */
export function RequireAuth({ children }: { readonly children: ReactNode }): JSX.Element {
  const { state } = useAuth();
  const location = useLocation();

  if (state.status === 'checking') {
    return (
      <div className="grid min-h-dvh place-items-center bg-page text-ink">
        <p role="status" className="text-sm text-ink-muted">
          Verificando sua sessão…
        </p>
      </div>
    );
  }

  if (state.status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }

  return <>{children}</>;
}
